import { Rate } from '../redux/ellipsisApi';
import store from '../redux/store';

export function getRate(fromCurrency: string) {
  const state = store.getState();

  const toCurrency = state.currency.currencyCode;
  const conversionRates = state.currency.rates;

  const rate =
    conversionRates.reduce((acc: any, curr: any) => {
      if (acc) return acc;
      if (curr.id === fromCurrency.toLowerCase()) return curr;
      else return null;
    }, null) ?? [];

  const rates = rate?.rates ?? [];

  return toCurrency === fromCurrency.toLowerCase()
    ? 1
    : (rates[toCurrency] ?? 1);
}

export function getDisplayRate(
  rates: Rate[],
  period: string[],
  experienceType?: string
): { price: number; postText: string } {
  const d = new Date();
  let defaultRate = Infinity;
  let rateBasis = '';
  for (const rate of rates ?? []) {
    if (
      (rate.validForever ||
        (rate.validFrom &&
          rate.validTo &&
          d >= new Date(rate.validFrom) &&
          d <= new Date(rate.validTo))) &&
      (!rate.userIds || rate.userIds?.length === 0) &&
      rate.value &&
      rate.value < defaultRate &&
      rate.period &&
      period.indexOf(rate.period) > -1
    ) {
      defaultRate = rate.value;
      rateBasis = rate.period;
    }
  }
  if (defaultRate === Infinity) {
    let closestValidFrom = Infinity;
    for (const rate of rates ?? []) {
      if (
        rate.validFrom &&
        rate.validTo &&
        (!rate.userIds || rate.userIds?.length === 0) &&
        rate.value &&
        new Date(rate.validTo) > d &&
        new Date(rate.validFrom).getTime() < closestValidFrom
      ) {
        closestValidFrom = new Date(rate.validFrom).getTime();
        defaultRate = rate.value;
        rateBasis = rate.period ?? '';
      }
    }

    if (defaultRate === Infinity) {
      return { price: 0, postText: '' };
    }
  }
  if (rateBasis === 'overnight') {
    rateBasis = 'night';
  }
  if (experienceType) {
    const adjustedPeriod =
      experienceType === 'exclusive'
        ? 'base'
        : experienceType === 'shared'
          ? 'per-person'
          : rateBasis;
    for (const rate of rates ?? []) {
      if (rate.period === adjustedPeriod && rate.value) {
        defaultRate = rate.value;
        rateBasis = adjustedPeriod === 'base' ? 'booking' : 'per person';
        break;
      }
    }
  }

  return { price: defaultRate, postText: `/ ${rateBasis}` };
}

export interface PublicRate {
  period: string;
  price: number;
}

export interface RateOption {
  key: string;
  label: string;
  detail?: string;
  price: number;
  suffix: string;
}

const isPublicRate = (rate: Rate) => !rate.userIds || rate.userIds.length === 0;

const isRateValidOn = (rate: Rate, date: Date) =>
  !!rate.validForever ||
  (!!rate.validFrom &&
    !!rate.validTo &&
    date >= new Date(rate.validFrom) &&
    date <= new Date(rate.validTo));

const isUpcomingRate = (rate: Rate, date: Date) =>
  !!rate.validFrom &&
  !!rate.validTo &&
  date < new Date(rate.validFrom) &&
  date < new Date(rate.validTo);

export function getPublicRates(rates: Rate[], periods: string[]): PublicRate[] {
  const now = new Date();
  const lowestActiveByPeriod = new Map<string, number>();
  const nearestUpcomingByPeriod = new Map<
    string,
    { validFrom: number; price: number }
  >();

  for (const rate of rates ?? []) {
    const value = Number(rate.value);
    if (!rate.period || !periods.includes(rate.period)) continue;
    if (!isPublicRate(rate) || !(value > 0)) continue;

    if (isRateValidOn(rate, now)) {
      const current = lowestActiveByPeriod.get(rate.period);
      if (current === undefined || value < current) {
        lowestActiveByPeriod.set(rate.period, value);
      }
      continue;
    }

    if (!isUpcomingRate(rate, now)) continue;

    const validFrom = new Date(rate.validFrom!).getTime();
    const current = nearestUpcomingByPeriod.get(rate.period);
    if (
      current === undefined ||
      validFrom < current.validFrom ||
      (validFrom === current.validFrom && value < current.price)
    ) {
      nearestUpcomingByPeriod.set(rate.period, { validFrom, price: value });
    }
  }

  return periods.flatMap((period) => {
    const activePrice = lowestActiveByPeriod.get(period);
    const upcoming = nearestUpcomingByPeriod.get(period);
    const price = activePrice ?? upcoming?.price;
    return price === undefined ? [] : [{ period, price }];
  });
}
